<script setup lang="ts">
import {
  Trash2 as Trash2Icon,
  MapPin as MapPinIcon,
  Image as ImageIcon,
  Clock as ClockIcon,
  RefreshCw as RefreshCwIcon,
  Check as CheckIcon,
} from '@lucide/vue'
import { storeToRefs } from 'pinia'
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'

import BaseProgressBar from '@/components/ui/BaseProgressBar.vue'
import { showConfirm } from '@/services/dialogService'
import { resolvePhotoUrl } from '@/services/offlinePhotoResolver'
import { requirePermission } from '@/services/permissionService'
import {
  ensureWorkspacePhotoAssetUrlsFresh,
  PHOTO_ASSET_URL_EXPIRY_OFFSET_MS,
} from '@/services/photoAssetUrlService'
import { syncService } from '@/services/syncService'
import { isTouchDevice } from '@/services/uiService'
import { useEventStore } from '@/stores/eventStore'
import { usePhotoStore } from '@/stores/photoStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { EventType } from '@/types/event'
import type { Photo } from '@/types/photo'
import { ConcurrencyLimiter } from '@/utils/concurrencyLimiter'
import { requireItemEditable } from '@/utils/permissionDialog'

const photoStore = usePhotoStore()
const workspaceStore = useWorkspaceStore()
const eventStore = useEventStore()
const { photosSortedByDate: photos } = storeToRefs(photoStore)

// Photo-adding progress (worker processing) — renders the previously-unwired photoStore
// progress state. Shown while a batch is being added.
const isAddingPhotos = computed(() => photoStore.isLoading && photoStore.totalToProcess > 0)
const addingLabel = computed(() => {
  const current = Math.min(photoStore.processingCount + 1, photoStore.totalToProcess)
  return `Adding photos ${current} of ${photoStore.totalToProcess}`
})

// Photos whose upload is still queued get a small "waiting to sync" clock badge; photos in
// the in-flight batch get a spinner instead (waiting → uploading → done, sync-feedback.md §4.3).
const pendingPhotoIds = computed(() => eventStore.pendingPhotoClientIds)
const uploadingPhotoIds = computed(() => {
  const inFlight = syncService.currentBatchClientIds.value
  if (inFlight.size === 0) return new Set<string>()
  const ids = new Set<string>()
  for (const event of eventStore.unsyncedEvents) {
    if (event.type !== EventType.PHOTO_CREATED || !inFlight.has(event.clientId)) continue
    const photoId = (event.payload as Record<string, unknown>).clientId
    if (typeof photoId === 'string') ids.add(photoId)
  }
  return ids
})

const props = defineProps<{
  /** When provided, overrides the store list with a filtered subset (e.g. map viewport filter). */
  overridePhotos?: Photo[]
  /** When true, clicking a photo selects it as the collection cover instead of opening it. */
  pickCoverMode?: boolean
  /** When false, hide delete actions. */
  canDeletePhotos?: boolean
  /** When false, hide set-cover actions. */
  canSetCoverPhoto?: boolean
  /** When true, the photo section is hidden on the map — dim the tiles to indicate it. */
  dimmed?: boolean
  /** Animate item removal in the Pinned system collection. */
  animatePinnedRemoval?: boolean
  selectionMode?: boolean
  selectedPhotoIds?: Set<string>
}>()

const displayPhotos = computed(() => props.overridePhotos ?? photos.value)

const emit = defineEmits<{
  'view-photo': [photoId: string]
  'zoom-photo': [photoId: string]
  'toggle-selection': [photoId: string]
}>()

const loadedThumbnails = ref(new Set<string>())
const observer = ref<IntersectionObserver | null>(null)
const thumbnailLimiter = new ConcurrencyLimiter(5)
let photoAssetRefreshIntervalId: number | null = null

const deletePhoto = async (photoId: string) => {
  if (!(await requirePermission('delete_photo'))) return
  const photo = photos.value.find((p) => p.clientId === photoId)
  if (!photo) return
  // Creator-only enforcement (with an explanatory dialog) also happens in
  // photoStore.clearPhoto, but checking here first avoids stacking it behind the "are you
  // sure" confirmation below.
  if (!(await requireItemEditable(photo.canEdit, 'photo', photo.uploadedByName))) return

  const photoName = photo.name || 'this photo'

  const confirmed = await showConfirm({
    title: 'Delete Photo',
    message: `Are you sure you want to delete "${photoName}"? This action cannot be undone.`,
    confirmText: 'Delete',
    cancelText: 'Cancel',
    variant: 'danger',
  })

  if (confirmed) {
    photoStore.clearPhoto(photoId)
  }
}

const viewPhoto = (photoId: string) => {
  emit('view-photo', photoId)
}

const zoomToPhoto = (photoId: string) => {
  const photo = photos.value.find((img) => img.clientId === photoId)
  if (photo && photo.hasCoordinates) {
    emit('zoom-photo', photoId)
  }
}

const getThumbnailSrc = (photoId: string) => {
  const photo = photos.value.find((p) => p.clientId === photoId)
  if (!photo) return ''

  if (!loadedThumbnails.value.has(photoId)) {
    return '' // Return empty src until loaded
  }

  return photo._resolvedOfflineThumbnailUrl || photo._thumbnailData || photo._thumbnailUrl || ''
}

const refreshThumbnailUrlsIfNeeded = async () => {
  if (!workspaceStore.clientId || displayPhotos.value.length === 0) return

  await ensureWorkspacePhotoAssetUrlsFresh(workspaceStore.clientId, displayPhotos.value, {
    variants: ['thumb'],
  })
}

const loadThumbnail = async (photoId: string, img: HTMLImageElement) => {
  if (loadedThumbnails.value.has(photoId)) return

  await thumbnailLimiter.execute(async () => {
    await refreshThumbnailUrlsIfNeeded()

    const photo = photos.value.find((p) => p.clientId === photoId)
    if (photo) {
      const rawSrc = photo._thumbnailData || photo._thumbnailUrl || ''
      if (!rawSrc) return

      const resolvedSrc = await resolvePhotoUrl(
        workspaceStore.clientId,
        photo.clientId,
        rawSrc,
        'thumb',
      )
      photo._resolvedOfflineThumbnailUrl = resolvedSrc
      img.src = resolvedSrc
      loadedThumbnails.value.add(photoId)
    }
  })
}

const setupIntersectionObserver = () => {
  observer.value = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement
          const photoId = img.dataset.photoId
          if (photoId && !loadedThumbnails.value.has(photoId)) {
            loadThumbnail(photoId, img)
          }
        }
      })
    },
    {
      root: null,
      rootMargin: '50px',
      threshold: 0.1,
    },
  )
}

// Created eagerly (not in onMounted) so the observer already exists by the time the
// v-for's image refs first mount — ref callbacks fire during the initial patch, which
// runs before onMounted, so a later setup here would miss observing the first batch.
setupIntersectionObserver()

onMounted(() => {
  void refreshThumbnailUrlsIfNeeded()
  photoAssetRefreshIntervalId = window.setInterval(
    () => void refreshThumbnailUrlsIfNeeded(),
    PHOTO_ASSET_URL_EXPIRY_OFFSET_MS,
  )
})

onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect()
  }
  if (photoAssetRefreshIntervalId !== null) {
    window.clearInterval(photoAssetRefreshIntervalId)
    photoAssetRefreshIntervalId = null
  }
})

watch(
  () => displayPhotos.value.map((photo) => photo.clientId).join('|'),
  () => {
    void refreshThumbnailUrlsIfNeeded()
  },
)

const onImageMount = (el: Element, photoId: string) => {
  if (observer.value && el instanceof HTMLImageElement) {
    el.dataset.photoId = photoId
    observer.value.observe(el)
  }
}

const hasPhotos = computed(() => displayPhotos.value.length > 0)
const isTouch = isTouchDevice()
</script>

<template>
  <div v-if="hasPhotos || isAddingPhotos" class="bg-white p-3 overflow-x-hidden">
    <!-- Adding-photos progress (worker processing of the current batch) -->
    <div v-if="isAddingPhotos" class="mb-2" data-testid="photo-adding-progress">
      <p class="mb-1 text-xs font-medium text-gray-600">{{ addingLabel }}</p>
      <BaseProgressBar :value="photoStore.processingProgress" />
    </div>

    <TransitionGroup
      tag="div"
      class="grid grid-cols-3 gap-1 transition-[opacity,filter]"
      :class="{ 'opacity-40 grayscale': props.dimmed }"
      :name="props.animatePinnedRemoval ? 'pinned-photo-unpin-slide' : undefined"
    >
      <div
        v-for="photo in displayPhotos"
        :key="photo.clientId"
        class="relative overflow-hidden bg-transparent border border-gray-200 shadow-sm hover:shadow-md transition-shadow group"
        :class="{ 'ring-2 ring-emerald-400 ring-inset': props.pickCoverMode }"
        :data-photo-id="photo.clientId"
      >
        <div
          class="aspect-square overflow-hidden relative"
          :class="props.pickCoverMode ? 'cursor-pointer' : 'cursor-pointer'"
          @click="
            props.selectionMode
              ? emit('toggle-selection', photo.clientId)
              : viewPhoto(photo.clientId)
          "
        >
          <!-- Placeholder that shows when thumbnail is not loaded -->
          <div
            v-if="!loadedThumbnails.has(photo.clientId)"
            class="absolute inset-0 w-full h-full bg-transparent z-10"
          ></div>

          <!-- Actual thumbnail image - always rendered for intersection observer -->
          <img
            :src="getThumbnailSrc(photo.clientId)"
            :alt="photo.name || 'Photo'"
            @click.stop="
              props.selectionMode
                ? emit('toggle-selection', photo.clientId)
                : viewPhoto(photo.clientId)
            "
            class="absolute inset-0 w-full h-full object-cover"
            :class="{
              'opacity-0': !loadedThumbnails.has(photo.clientId),
              'z-dropdown': loadedThumbnails.has(photo.clientId),
            }"
            :ref="(el) => onImageMount(el as Element, photo.clientId)"
          />
        </div>

        <button
          v-if="props.selectionMode"
          type="button"
          class="absolute left-1 top-1 z-30 flex h-6 w-6 items-center justify-center rounded-full border border-white bg-white shadow-sm transition"
          :class="
            props.selectedPhotoIds?.has(photo.clientId) ? 'text-emerald-700' : 'text-transparent'
          "
          aria-label="Select photo"
          @click.stop="emit('toggle-selection', photo.clientId)"
        >
          <CheckIcon class="h-4 w-4" />
        </button>

        <!-- Sync badge: spinner while this photo's upload is in flight, clock while queued -->
        <div
          v-if="pendingPhotoIds.has(photo.clientId)"
          class="absolute bottom-1 right-1 z-20 flex h-5 w-5 items-center justify-center rounded-full bg-white/85 shadow-sm pointer-events-none"
          :title="uploadingPhotoIds.has(photo.clientId) ? 'Uploading…' : 'Waiting to sync'"
          data-testid="photo-pending-badge"
        >
          <RefreshCwIcon
            v-if="uploadingPhotoIds.has(photo.clientId)"
            class="h-3.5 w-3.5 animate-spin text-blue-600"
          />
          <ClockIcon v-else class="h-3.5 w-3.5 text-gray-600" />
        </div>

        <!-- Semi-transparent gradient overlay for better icon visibility -->
        <div
          :class="[
            'absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/50 to-transparent transition-opacity duration-200 pointer-events-none',
            isTouch ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
          ]"
        ></div>

        <!-- Action buttons overlay — hidden in pick-cover mode -->
        <!-- Delete button - bottom-left corner -->
        <button
          v-if="!props.pickCoverMode && props.canDeletePhotos !== false"
          @click.stop="deletePhoto(photo.clientId)"
          :class="[
            'absolute bottom-1 left-1 p-1 text-white hover:text-gray-300 transition-opacity duration-200 hover:cursor-pointer',
            isTouch ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
          ]"
          title="Delete photo"
          aria-label="Delete photo"
        >
          <Trash2Icon class="w-4 h-4" />
        </button>

        <!-- Set as cover button - bottom-right corner (when not already in pick-cover mode and can edit) -->
        <button
          v-if="!props.pickCoverMode && props.canSetCoverPhoto !== false"
          @click.stop="workspaceStore.setCoverPhoto(photo.clientId)"
          :class="[
            'absolute bottom-1 right-1 p-1 text-white hover:text-gray-300 transition-opacity duration-200 hover:cursor-pointer',
            isTouch ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
          ]"
          title="Set as collection cover"
          aria-label="Set as collection cover"
        >
          <ImageIcon class="w-4 h-4" />
        </button>

        <!-- Zoom to location button — top-right for contributors (cover button takes bottom-right), bottom-right for viewers -->
        <button
          v-if="photo.hasCoordinates && !props.pickCoverMode"
          @click.stop="zoomToPhoto(photo.clientId)"
          :class="[
            'absolute p-1 text-white hover:text-gray-300 transition-opacity duration-200 hover:cursor-pointer',
            workspaceStore.canEditWorkspace ? 'top-1 right-1' : 'bottom-1 right-1',
            isTouch ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
          ]"
          title="Zoom to photo on map"
          aria-label="Zoom to photo location on map"
        >
          <MapPinIcon class="w-4 h-4" />
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.pinned-photo-unpin-slide-leave-active {
  transition:
    opacity 180ms ease,
    transform 180ms cubic-bezier(0.4, 0, 0.2, 1);
}

.pinned-photo-unpin-slide-move {
  transition: transform 180ms cubic-bezier(0.4, 0, 0.2, 1);
}

.pinned-photo-unpin-slide-leave-to {
  opacity: 0;
  transform: translateY(-14px);
}
</style>
